import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Log, IShortLogger } from './logger.service';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class ResourceMasterDataService {
  private readonly logger: IShortLogger<ResourceMasterDataService> = Log.create(ResourceMasterDataService);
  private masterData: any;
  private queryCache: Map<string, any> = new Map();
  public serviceUrl: string = environment.apiUrl;

  constructor(private configService: ConfigService) {
    this.configService.configLoaded$.subscribe(async loaded => {
      this.logger.d("Config loaded event received. Loaded: ", loaded);
      if (loaded) {
        this.serviceUrl = this.configService.getApiUrl();
        this.logger.d("Service URL set to: ", this.serviceUrl);
        await this.loadMasterDataFromApi();
        this.logger.d("Master data loaded successfully");
        this.logger.d('Master Data: ', this.masterData);
      }
    });
  }

  /**
   * Get data from the master data store using dot notation path
   * Uses a cache to improve performance for repeated queries
   * @param path Path to the data using dot notation (e.g., 'hardware.cpus')
   * @returns The requested data or undefined if not found
   * @example
   * service.get<number[]>(`hardware.ram`)
   * service.get<Pair[]>(`software.os.win11`)
   */
  get<T>(path: string): T | undefined {
    // Check if we have a cached result for this path
    if (this.queryCache.has(path)) {
      return this.queryCache.get(path) as T;
    }

    // Handle empty path (return entire data set)
    if (!path) {
      const result = this.masterData as T;
      this.queryCache.set(path, result);
      return result;
    }

    // Traverse the data using the path
    const parts = path.split('.');
    let current = this.masterData;

    for (const part of parts) {
      if (current === undefined || current === null) {
        // Cache the undefined result too
        this.queryCache.set(path, undefined);
        return undefined;
      }
      current = current[part];
    }

    // Cache the result before returning
    this.queryCache.set(path, current);
    return current as T;
  }

  /**
   * Check if a path exists in the master data
   * @param path Path to check
   * @returns True if the path exists and is not undefined
   */
  has(path: string): boolean {
    return this.get<any>(path) !== undefined;
  }

  /**
   * Clear the query cache
   * Should be called after updating the master data
   */
  clearCache(): void {
    this.queryCache.clear();
  }

  /**
   * Update the master data and clear the cache
   * @param newData The new master data
   */
  updateMasterData(newData: any): void {
    this.masterData = newData;
    this.clearCache();
  }

  /**
   * Load master data from an API - would be used in a real application
   * For now, this is just a placeholder
   */
  public async loadMasterDataFromApi(): Promise<void> {
    try {
      const response = await fetch(`${this.serviceUrl}/v1/resources/master`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      this.masterData = await response.json();
      this.clearCache(); // Clear cache after updating data
      this.logger.d("Master data loaded:", this.masterData);
    } catch (error) {
      this.logger.e('Failed to load master data:', error);
    }
  }
}
